/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ArrayExtensions, getFallbackImageUrl, getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, INodeProps, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { CartLineItemComponent } from '@msdyn365-commerce/components';
import { Image } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, ProductDeliveryOptions, SalesLine, Shipment, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import Address from './address';
import LabelComponent from './label';
import { defaultImageSettings, getDateTimeslot, getSalesStatusCode, IDeliveryType, IModuleComponentProps, ISalesStatusCode } from './utilities';

export type ISalesStatusStringMap = {
    [x in ISalesStatusCode]: string | undefined;
};

export type IDeliveryTypeStringMap = {
    [x in IDeliveryType]: string | undefined;
};

export interface ISalesLineResources {
    productDimensionTypeSize: string;
    productDimensionTypeColor: string;
    productDimensionTypeStyle: string;
    productDimensionTypeAmount?: string;
    salesLineQuantityText: string;
    configString: string;
    shipToLabel: string;
    storeLabel: string;
    phoneLabel: string;
    phoneAriaLabel: string;
    buyItAgainAriaLabel: string;
    buyItAgainLabel: string;
    orderStatusReadyForPickup: string;
    orderStatusPickedUp: string;
    orderStatusShipped: string;
    orderStatusCanceled: string;
    orderStatusProcessing: string;
    pickedUpSalesLines: string;
    deliveredSalesLines: string;
    carryOutSalesLines: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    discountStringText: string;
    orderStatusEmailed: string;
    emailSalesLines: string;
    pickupDateTimeslotText: string;
    pickupTimeslotPlaceHolder: string;
    shippingCharges?: string;
}

export interface IGetSalesLineInput {
    moduleName: string;
    moduleComponentProps: IModuleComponentProps;
    product: SimpleProduct;
    shipment?: Shipment;
    salesLine: SalesLine;
    isCurrentChannel: boolean;
    resources: ISalesLineResources;
    imageOnly?: boolean;
    showCompleteAddress?: boolean;
    showTimeslot: boolean;
    telemetryContent?: ITelemetryContent;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    productDeliveryOptions?: ProductDeliveryOptions[];
}

export interface ISalesLine {
    salesLineProps: INodeProps;
    salesLine: React.ReactNode;
    address?: React.ReactNode;
    buyAgainButton?: React.ReactNode;
    salesStatus: React.ReactNode;
    deliveryType: React.ReactNode;
    trackingInfo: React.ReactNode;
    pickupDateTimeslot: React.ReactNode;

    // row data
    data: {
        salesLine: SalesLine;
        product: SimpleProduct;
        shipment?: Shipment;
        deliveryType: string;
        productUrl: string;
        salesStatus: ISalesStatusCode;
    };
}

const enum deliveryTypeEnum {
    ship = 'ship',
    email = 'email',
    carryOut = 'carryOut',
    pickUp = 'pickUp'
}

// tslint:disable:max-func-body-length
// tslint:disable-next-line:cyclomatic-complexity
export const getSalesLine = ({
    moduleName,
    moduleComponentProps: {
        id,
        typeName,
        context,
        telemetry,
        config: { imageSettings, showShippingChargesForLineItems }
    },
    showCompleteAddress,
    product,
    shipment,
    salesLine,
    isCurrentChannel,
    imageOnly,
    showTimeslot,
    resources: {
        productDimensionTypeSize: sizeString,
        productDimensionTypeColor: colorString,
        productDimensionTypeStyle: styleString,
        productDimensionTypeAmount: amountString,
        salesLineQuantityText: quantityDisplayString,
        configString,
        shipToLabel,
        storeLabel,
        phoneLabel,
        phoneAriaLabel,
        buyItAgainLabel,
        buyItAgainAriaLabel,
        orderStatusReadyForPickup,
        orderStatusProcessing,
        orderStatusShipped,
        orderStatusPickedUp,
        orderStatusCanceled,
        orderStatusEmailed,
        pickedUpSalesLines,
        deliveredSalesLines,
        carryOutSalesLines,
        emailSalesLines,
        trackingLabel,
        trackingAriaLabel,
        trackingComingSoonLabel,
        discountStringText,
        pickupDateTimeslotText,
        pickupTimeslotPlaceHolder,
        shippingCharges
    },
    telemetryContent,
    retailMultiplePickUpOptionEnabled,
    channelDeliveryOptionConfig,
    productDeliveryOptions
}: IGetSalesLineInput): ISalesLine => {
    const salesStatusStringMap: ISalesStatusStringMap = {
        canceled: orderStatusCanceled,
        readyForPickup: orderStatusReadyForPickup,
        pickedUp: orderStatusPickedUp,
        processing: orderStatusProcessing,
        shipped: orderStatusShipped,
        unknown: undefined,
        emailed: orderStatusEmailed
    };

    const deliveryTypeStringMap: IDeliveryTypeStringMap = {
        pickUp: pickedUpSalesLines,
        ship: deliveredSalesLines,
        carryOut: carryOutSalesLines,
        email: emailSalesLines
    };

    const gridSettings = context.request.gridSettings;
    const pickupDeliveryModeCode = context.request.channel && context.request.channel.PickupDeliveryModeCode;
    const emailDeliveryModeCode = context.request.channel && context.request.channel.EmailDeliveryModeCode;
    const productUrl = getProductUrlSync(product, context.actionContext, undefined);
    const pickupMode = getPickupModeDesc(product, salesLine.DeliveryMode, productDeliveryOptions, retailMultiplePickUpOptionEnabled);
    const deliveryType = getDeliveryType(pickupMode, salesLine, salesLine.DeliveryMode, pickupDeliveryModeCode, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig, emailDeliveryModeCode);
    const salesStatus = getSalesStatusCode(deliveryType, salesLine.SalesStatusValue, pickupMode);
    const salesStatusString = salesStatusStringMap[salesStatus];
    const salesStatusComp = salesStatusString && <LabelComponent className={`${moduleName}__sales-status`} label={salesStatusString} />;
    const deliveryTypeString = getDeliveryTypeString(deliveryType, deliveryTypeStringMap, retailMultiplePickUpOptionEnabled);
    const deliveryTypeClass = getDeliveryTypeClassName(deliveryType, deliveryTypeStringMap);
    const storePickUp = retailMultiplePickUpOptionEnabled ? getStoreName(salesLine, deliveryType) : null;
    const deliveryTypeComp = retailMultiplePickUpOptionEnabled ? deliveryTypeString && <LabelComponent className={`${moduleName}__devliery-type ${deliveryTypeClass}-icon`} label= {`${deliveryTypeString} ${storePickUp}`} />
                             : deliveryTypeString && <LabelComponent className={`${moduleName}__devliery-type`} label={deliveryTypeString} />;
    const trackingInfo = isCurrentChannel && deliveryType === deliveryTypeEnum.ship && (
        <span className={`${moduleName}__tracking-info`}>
            {trackingLabel}
            {shipment ? (
                <a className={`${moduleName}__tracking-link`} href={shipment.TrackingUrl} aria-label={trackingAriaLabel}>
                    {shipment.TrackingNumber}
                </a>
            ) : (
                    <>{trackingComingSoonLabel}</>
                )}
        </span>
    );
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.BuyItAgain);
    const buyItAgainAttributes = getTelemetryAttributes(telemetryContent!, payLoad);
    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);

    const canShowTimeslot: boolean = showTimeslot
        && deliveryType === pickupMode
        && salesLine.PickupTimeslotStartDateTime !== undefined
        && salesLine.PickupTimeslotEndDateTime !== undefined;

    const pickupDateTimeslot = canShowTimeslot ? getDateTimeslot(pickupTimeslotPlaceHolder, salesLine.PickupTimeslotStartDateTime, salesLine.PickupTimeslotEndDateTime, context.request.locale) : '';
    const pickupDateTimeslotInfo = canShowTimeslot && (
        <div className={`${moduleName}__pickup-date-timeslot`}>
            <div className={`${moduleName}__pickup-date-timeslot-title`}>{pickupDateTimeslotText}</div>
            <div className={`${moduleName}__pickup-date-timeslot-content`}>{pickupDateTimeslot}</div>
        </div>
    );
    if(imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    return {
        data: {
            salesLine,
            productUrl,
            deliveryType,
            salesStatus,
            product,
            shipment
        },
        salesLineProps: { className: `${moduleName}__sales-line` },
        salesLine: imageOnly ? (
            <div className={`${moduleName}__sales-line-picture`}>
                <Image
                    className={`${moduleName}__sales-line-image`}
                    altText={product.Name}
                    src={product.PrimaryImageUrl || ''}
                    fallBackSrc={fallbackImageUrl}
                    gridSettings={gridSettings!}
                    imageSettings={imageSettings || defaultImageSettings}
                    loadFailureBehavior='empty'
                />
            </div>
        ) : (
                <CartLineItemComponent
                    id={id}
                    typeName={typeName}
                    context={context}
                    resources={{
                        sizeString,
                        colorString,
                        styleString,
                        amountString,
                        quantityDisplayString,
                        configString,
                        discountStringText,
                        inputQuantityAriaLabel: '',
                        originalPriceText: '',
                        currentPriceText: '',
                        shippingChargesText: shippingCharges || ''
                    }}
                    primaryImageUrl={product.PrimaryImageUrl}
                    productUrl={productUrl}
                    imageSettings={imageSettings || defaultImageSettings}
                    gridSettings={gridSettings!}
                    isSalesLine={true}
                    data={{ cartLine: salesLine, product: product }}
                    showShippingChargesForLineItems = {showShippingChargesForLineItems}
                    channelDeliveryOptionConfig = {retailMultiplePickUpOptionEnabled? channelDeliveryOptionConfig: undefined}
                />
            ),
        address: salesLine.ShippingAddress && (
            <Address
                moduleName={moduleName}
                address={salesLine.ShippingAddress}
                resource={{ shipToLabel, storeLabel, phoneLabel, phoneAriaLabel }}
                deliveryType={deliveryType}
                showCompleteAddress={showCompleteAddress}
            />
        ),
        deliveryType: deliveryTypeComp,
        salesStatus: salesStatusComp,
        trackingInfo,
        buyAgainButton: retailMultiplePickUpOptionEnabled ? isCurrentChannel && (
            <a
                href={productUrl}
                aria-label={product?.Name && buyItAgainAriaLabel ? (buyItAgainAriaLabel || '').replace('{productName}', product?.Name) : ''}
                className={`${moduleName}__sales-line-btn-buy-it-again-link`}
                {...buyItAgainAttributes}
            >
                {buyItAgainLabel}
            </a>
        ) :
        isCurrentChannel && (
            <Button
                href={productUrl}
                aria-label={product?.Name && buyItAgainAriaLabel ? (buyItAgainAriaLabel || '').replace('{productName}', product?.Name) : ''}
                className={`${moduleName}__sales-line-btn-buy-it-again`}
                {...buyItAgainAttributes}
            >
                {buyItAgainLabel}
            </Button>
        ),
        pickupDateTimeslot: pickupDateTimeslotInfo
    };
};

export const getDeliveryType  = (pickupMode: string, salesLine: SalesLine, salesLineDeliveryMode?: string, pickupDeliveryModeCode?: string, retailMultiplePickupOption?: boolean,
                                 channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,  emailDeliveryModeCode?: string): string => {
        let isPickupDeliveryMode: boolean;

        if (retailMultiplePickupOption) {
            isPickupDeliveryMode = salesLineDeliveryMode === channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(dM => dM === salesLineDeliveryMode);
        } else {
            isPickupDeliveryMode = !!pickupDeliveryModeCode && pickupDeliveryModeCode !== '' && pickupDeliveryModeCode === salesLineDeliveryMode;
        }

        return isPickupDeliveryMode
            ? pickupMode
            : !!emailDeliveryModeCode && emailDeliveryModeCode !== '' && emailDeliveryModeCode === salesLine.DeliveryMode
                ? deliveryTypeEnum.email
                : !!salesLine.DeliveryMode
                    ? deliveryTypeEnum.ship
                    : deliveryTypeEnum.carryOut;
};

export const getDeliveryTypeString = (deliveryType: string, deliveryTypeStringMap: IDeliveryTypeStringMap , retailMultiplePickupOption?: boolean): string | undefined=> {
    if(retailMultiplePickupOption) {
        switch (deliveryType) {
            case deliveryTypeEnum.ship:
            case deliveryTypeEnum.carryOut:
            case deliveryTypeEnum.email:
                return deliveryTypeStringMap[deliveryType];
            default:
                return deliveryType;
        }
    } else {
       return deliveryTypeStringMap[deliveryType];
    }
};

export const getDeliveryTypeClassName = (deliveryType: string, deliveryTypeStringMap: IDeliveryTypeStringMap , retailMultiplePickupOption?: boolean): string | undefined=> {
        switch (deliveryType) {
            case deliveryTypeEnum.ship:
            case deliveryTypeEnum.carryOut:
            case deliveryTypeEnum.email:
                return deliveryType;
            default:
                return deliveryTypeEnum.pickUp;
        }
};

export const getPickupModeDesc = (product: SimpleProduct, salesLineDeliveryMode?: string, productDeliveryOptions?: ProductDeliveryOptions[], retailMultiplePickupOption?: boolean): string => {
    let deliveryOption: string = 'pickup';
    if (ArrayExtensions.hasElements(productDeliveryOptions) && product !== undefined) {
        let foundProductDeliveryOption;
        foundProductDeliveryOption = productDeliveryOptions?.find((deliveryO) => {
            return deliveryO && deliveryO.ProductId === product.RecordId;
        });
        const deliveryDesc = foundProductDeliveryOption?.DeliveryOptions ? foundProductDeliveryOption?.DeliveryOptions?.find(option => option.Code === salesLineDeliveryMode)?.Description: deliveryOption;
        if (deliveryDesc !== undefined) {
            deliveryOption = deliveryDesc;
        }
    }
    return deliveryOption;
};

export const getStoreName = (salesLine: SalesLine, deliveryType?: string): string | undefined => {
   switch (deliveryType) {
        case deliveryTypeEnum.ship:
        case deliveryTypeEnum.email:
        case deliveryTypeEnum.carryOut:
              return '';
       default:
           return `, ${salesLine.ShippingAddress?.Name}`;
   }
};